import React, { useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { XClose, SearchLg, FilterLines } from '@untitled-ui/icons-react'
import {
  MyModal,
  MyBgPatternDecorativeCircle,
  MyButton,
  MyTextField,
} from '@interstellar-component'
import MyClientFilterModal from '@src/components/MyClientFilterModal'
import { useWorkforce } from '../../../../../../Context'

// Condition operators reused per field. Static config because the credit
// utilization table is filtered purely on the client (no endpoint), mirroring
// the shape MyFilterModal expects from the API (`{ label, value }`).
const STRING_OPS = [
  { label: 'contains', value: 'like' },
  { label: 'equals', value: '=' },
]
const NUMBER_OPS = [
  { label: 'equals', value: '=' },
  { label: 'greater than', value: '>' },
  { label: 'less than', value: '<' },
]

// Field definitions handed to MyFilterModal. Number fields point at the raw
// (`*Raw`) value so comparisons run on real numbers, not the "Rp 2,000,000"
// display string. Penggunaan is already a numeric percentage.
const CREDIT_UTILIZATION_FILTERS = [
  { name: 'pelapor', label: 'Reporting institution', type: 'string', options: STRING_OPS },
  { name: 'jenis', label: 'Usage type', type: 'string', options: STRING_OPS },
  { name: 'bakiDebetRaw', label: 'Outstanding balance', type: 'number', options: NUMBER_OPS },
  { name: 'plafonRaw', label: 'Credit limit', type: 'number', options: NUMBER_OPS },
  { name: 'penggunaan', label: 'Utilization (%)', type: 'number', options: NUMBER_OPS },
]

function formatIDR(n) {
  const v = Number(n) || 0
  return `Rp ${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

// Evaluate a single MyFilterModal condition against one facility row.
function matchCondition(row, cond) {
  const op = cond?.condition?.value
  const raw = row[cond?.field]

  if (cond?.filter?.type === 'number') {
    const actual = Number(raw)
    const target = Number(cond?.value)
    if (!Number.isFinite(actual) || !Number.isFinite(target)) return false
    if (op === '>') return actual > target
    if (op === '<') return actual < target
    return actual === target // 'equals'
  }

  const fieldValue = String(raw ?? '').toLowerCase()
  const condValue = String(cond?.value ?? '').toLowerCase()
  if (op === '=') return fieldValue === condValue
  return fieldValue.includes(condValue) // 'like'
}

export default function CreditUtilizationModal({ open, onClose }) {
  const { workforceDetailReport } = useWorkforce()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState([])

  // Per-facility breakdown comes from credit_overview.credit_facilities,
  // populated by core/service/credit-report/builder.js#deriveCreditFacilities
  // (one row per granted contract). Empty array if the snapshot pre-dates v13.
  const allData = useMemo(() => {
    const facilities = workforceDetailReport?.credit_overview?.credit_facilities ?? []
    return facilities.map((f, i) => ({
      id: i + 1,
      pelapor: f.provider || '—',
      providerType: f.provider_type || '',
      jenis: f.contract_type || '—',
      bakiDebet: formatIDR(f.debit_balance),
      plafon: formatIDR(f.credit_limit),
      penggunaan: Number.isFinite(Number(f.utilization_pct)) ? Number(f.utilization_pct) : 0,
      // Raw values kept for numeric filtering / comparisons.
      bakiDebetRaw: Number(f.debit_balance) || 0,
      plafonRaw: Number(f.credit_limit) || 0,
    }))
  }, [workforceDetailReport])

  const searched = useMemo(() => {
    if (!search) return allData
    const q = search.toLowerCase()
    return allData.filter(
      (item) => item.pelapor.toLowerCase().includes(q) || item.jenis.toLowerCase().includes(q)
    )
  }, [allData, search])

  // Apply MyFilterModal conditions on top of the search result. The combinator
  // (and/or) is global in MyFilterModal — every condition shares the operator
  // set on the second row — so AND => every, OR => some.
  const filteredData = useMemo(() => {
    if (!filters.length) return searched
    const combinator = filters.find((c) => c?.operator?.value)?.operator?.value || 'and'
    return searched.filter((row) =>
      combinator === 'or'
        ? filters.some((c) => matchCondition(row, c))
        : filters.every((c) => matchCondition(row, c))
    )
  }, [searched, filters])

  const onSearchChange = useMemo(() => debounce((e) => setSearch(e.target.value ?? ''), 500), [])

  const hasActiveFilter = Boolean(search) || filters.length > 0

  return (
    <MyModal open={open} onClose={onClose} forceBlur maxWidth={1000}>
      <div className="flex flex-col bg-white rounded-xl shadow-xl overflow-hidden relative max-h-[90vh] w-full">
        {/* Background Pattern */}
        <div className="absolute top-0 left-0 pointer-events-none opacity-20 transform -translate-x-1/4 -translate-y-1/4 z-0">
          <MyBgPatternDecorativeCircle />
        </div>

        {/* Fixed Header */}
        <header className="relative pt-10 pb-6 px-10 flex justify-between items-start z-20 shrink-0 bg-white/80 backdrop-blur-sm">
          <div className="flex flex-col gap-1">
            <h3 className="text-[24px] font-semibold text-gray-900">Credit Utilization</h3>
            <p className="text-[16px] text-gray-500">
              Insight into credit exposure and utilization levels to assess financial risk.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <XClose className="w-6 h-6 text-gray-400" />
          </button>
        </header>

        {/* Scrollable Content Area */}
        <section
          className="flex-1 overflow-y-auto px-6 pb-6 z-10 custom-scrollbar min-h-0"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#D0D5DD transparent',
          }}
        >
          <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
            {/* Table Header / Toolbar */}
            <div className="flex p-4 items-center justify-between border-b border-gray-200 gap-4 sticky top-0 bg-white z-20">
              <div className="w-full max-w-sm">
                <MyTextField
                  placeholder="Search"
                  startAdornment={
                    <SearchLg className="size-5 text-gray-light/600" stroke="currentColor" />
                  }
                  focusColor="#14378E"
                  onChangeForm={onSearchChange}
                />
              </div>
              {/* MyFilterModal's popover (and its autocomplete/calendar
                  dropdowns) portal to document.body — outside #my-modal-children.
                  React still bubbles their click events up the React tree to
                  MyModal's outside-click handler, which would read the portaled
                  target as "outside" and close the whole modal. Stop click
                  propagation here so interacting with the filter never closes
                  the Credit Utilization modal. This div is a pure event-containment
                  wrapper, not an interactive control, so the a11y handlers don't apply. */}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
              <div onClick={(e) => e.stopPropagation()}>
                <MyClientFilterModal
                  id="filter-credit-utilization"
                  currentFilters={CREDIT_UTILIZATION_FILTERS}
                  onChange={(filter) =>
                    setFilters(Array.isArray(filter) ? filter : Object.values(filter || {}))
                  }
                  target={(popoverOpen, handleClick) => (
                    <MyButton color="gray" variant="tertiary" size="md" customClassname="gap-2 text-gray-700" onClick={handleClick}>
                      <FilterLines className="h-5 w-5 text-gray-700" />
                      Filters
                    </MyButton>
                  )}
                />
              </div>
            </div>

            {/* Data Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-600">
                <thead className="bg-[#f9fafb] text-gray-500 font-medium border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3">Reporting institution</th>
                    <th className="px-6 py-3">Usage type</th>
                    <th className="px-6 py-3">Outstanding balance</th>
                    <th className="px-6 py-3">Credit limit</th>
                    <th className="px-6 py-3">Utilization</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12">
                        <div className="flex flex-col items-center gap-2 text-center">
                          <p className="text-sm font-medium text-gray-900">No credit utilization</p>
                          <p className="text-xs text-gray-500 max-w-sm">
                            {hasActiveFilter
                              ? 'No records match your search or filters. Try adjusting them.'
                              : 'CLIK reported no credit facilities for this individual.'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-semibold text-gray-900">{row.pelapor}</span>
                            <span className="text-sm text-gray-500">{row.id}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{row.jenis}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 font-medium">{row.bakiDebet}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 font-medium">{row.plafon}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-2 w-24 rounded-full bg-gray-100 overflow-hidden min-w-[96px]">
                              <div
                                className="h-full bg-brand/900 rounded-full"
                                style={{ width: `${row.penggunaan}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-700">{row.penggunaan}%</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="flex items-center px-6 py-4 border-t border-gray-200">
              <span className="text-sm text-gray-600 font-medium">
                {filteredData.length} record{filteredData.length === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        </section>
      </div>
    </MyModal>
  )
}
